import React, { Context, createContext, Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { fetchLocation, fetchStores } from './util/api';
import { useSearchParams } from 'react-router-dom';
import { Store } from './types';
import Component from './components/templates';
import { EXCLUDED_REGIONS, TEMPLATE } from './config';
import { normalizeLocation } from './util/misc';

interface FeatureFilter {
    code: string;
    value: string | null;
}

export interface LocationFilters {
    country: string;
    region: string;
    city: string;
}

export type PartialLocation = Partial<LocationFilters> | null;

export interface StoreState {
    stores: Store[];
    allStores: Store[];
    locationFilters: LocationFilters;
    setLocationFilters: Dispatch<SetStateAction<LocationFilters>>;
    featureFilters: FeatureFilter[];
    setFeatureFilters: Dispatch<SetStateAction<FeatureFilter[]>>;
    search: string;
    setSearch: Dispatch<SetStateAction<string>>;
    featureSearch: string[];
    setFeatureSearch: Dispatch<SetStateAction<string[]>>;
    multipleSearch: string[];
    setMultipleSearch: Dispatch<SetStateAction<string[]>>;
    filterStock: boolean;
    setFilterStock: Dispatch<SetStateAction<boolean>>;
    article: string | null;
    locationToSet: PartialLocation;
    setLocationToSet: Dispatch<SetStateAction<PartialLocation>>;
    activeFilter: string | null;
    setActiveFilter: Dispatch<SetStateAction<string | null>>;
}

const locationKey = 'where_to_buy.location';
const storedLocation = localStorage.getItem(locationKey);
const blankLocation = { country: '', region: '', city: '' };
const defaultLocation: LocationFilters = storedLocation ? JSON.parse(storedLocation) : blankLocation;

export const StoresContext: Context<StoreState> = createContext({
    stores: [],
    allStores: [],
    locationFilters: defaultLocation,
    setLocationFilters: () => {},
    featureFilters: [],
    setFeatureFilters: () => {},
    search: '',
    setSearch: () => {},
    featureSearch: [],
    setFeatureSearch: () => {},
    multipleSearch: [],
    setMultipleSearch: () => {},
    filterStock: false,
    setFilterStock: () => {},
    article: null,
    locationToSet: null,
    setLocationToSet: () => {},
    activeFilter: null,
    setActiveFilter: () => {},
} as StoreState);

function App() {
    const [stores, setStores] = useState<Store[]>([]);
    const [locationFilters, setLocationFilters] = useState(defaultLocation);
    const [featureFilters, setFeatureFilters] = useState<FeatureFilter[]>([]);
    const [search, setSearch] = useState('');
    const [featureSearch, setFeatureSearch] = useState<string[]>([]);
    const [multipleSearch, setMultipleSearch] = useState<string[]>([]);
    const [params] = useSearchParams();
    const [locationToSet, setLocationToSet] = useState<Partial<LocationFilters> | null>(null);
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const article = useMemo(() => params.get('article'), [params]);
    const [filterStock, setFilterStock] = useState(!!article);

    const filteredStores = [];
    outer: for (const s of stores) {
        if (!!s.region && EXCLUDED_REGIONS.includes(s.region)) continue
        if (filterStock && !s.product?.stock) {
            continue;
        }
        for (const [key, value] of Object.entries(locationFilters)) {
            if (value && s[key as keyof typeof locationFilters] !== value) {
                continue outer;
            }
        }

        if (activeFilter === 'engineering') {
            const hasEngineeringFeature = Object.keys(s.featuresByCode).some((code) =>
                code.startsWith('ENG')
            );
            if (!hasEngineeringFeature) {
                continue;
            }
        }

        for (const { code, value } of featureFilters) {
            if (!s.featuresByCode[code]) {
                continue outer;
            }
            if (value !== '_any_' && s.featuresByCode[code].value !== value) {
                continue outer;
            }
        }

        // Применяем поиск только к уже отфильтрованным данным
        if (search && search.length) {
            // Текстовый поиск - проверяем точное вхождение
            if (!s.searchable.includes(search)) {
                continue;
            }
        }

        if (featureSearch && featureSearch.length) {
            const searchableLower = s.searchable.toLowerCase();
            let isSearched = false;
            for (const value of featureSearch) {
                if (!!value && searchableLower.includes(value.toLowerCase())) {
                    isSearched = true;
                    break;
                }
            }
            if (!isSearched) {
                continue;
            }
        }

        // Поддержка старой логики multipleSearch для совместимости
        if (multipleSearch && multipleSearch.length) {
            let isSearched = false;
            for (const value of multipleSearch) {
                if (!!value && s.searchable.includes(value)) {
                    isSearched = true;
                }
            }

            if (!isSearched) {
                continue;
            }
        }

        filteredStores.push(s);
    }
    useEffect(() => {
        // только на определенных сайтах
        if (!['new_iek'].includes(TEMPLATE)) {
            return;
        }
        // только если пользователь раньше не заходил
        if (storedLocation) {
            return;
        }
        fetchLocation()
            .then((location) => {
                setLocationToSet({
                    country: location.country ? normalizeLocation(location.country) : undefined,
                    region: location.region ? normalizeLocation(location.region) : undefined,
                    city: location.city ? normalizeLocation(location.city) : undefined,
                });
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        fetchStores(article).then((stores) => {
            setStores(stores);
        });
    }, [article]);

    useEffect(() => {
        localStorage.setItem(locationKey, JSON.stringify(locationFilters));
    }, [locationFilters]);

    return (
        <StoresContext.Provider
            value={{
                stores: filteredStores,
                allStores: stores,
                locationFilters,
                setLocationFilters,
                featureFilters,
                setFeatureFilters,
                search,
                setSearch,
                featureSearch,
                setFeatureSearch,
                multipleSearch,
                setMultipleSearch,
                filterStock,
                setFilterStock,
                article,
                locationToSet,
                setLocationToSet,
                activeFilter,
                setActiveFilter,
            }}
        >
            <Component />
        </StoresContext.Provider>
    );
}

export default App;
